import { GameConfigService } from '@services/core/GameConfigService';
import { TranslationService } from '@services/core/TranslationService';
import Phaser from 'phaser';
import { DPR } from '@constants/common.const';

export interface ButtonIconConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  iconKey: string;
  iconSize?: number;
  text?: string;
  isDisabled?: boolean;
  textStyle?: Phaser.Types.GameObjects.Text.TextStyle;
  onClick?: () => void | Promise<void>;
  // Called when the button is disabled and the user tries to click/tap it.
  onDisabledClick?: () => void | Promise<void>;
  hitRadius?: number;
}

export class ButtonWithIcon extends Phaser.GameObjects.Container {
  private icon: Phaser.GameObjects.Image;
  private label?: Phaser.GameObjects.Text;
  private config: ButtonIconConfig;
  private iconSize = 24 * DPR;
  private readonly fontSize = 10 * DPR;

  constructor(config: ButtonIconConfig) {
    super(config.scene, config.x, config.y);
    this.config = config;
    if (config.iconSize) {
      this.iconSize = config.iconSize;
    }

    this.icon = config.scene.add
      .image(0, 0, config.iconKey)
      .setDisplaySize(this.iconSize, this.iconSize)
      .setOrigin(0.5);

    this.add(this.icon);

    const offsetY = this.iconSize / 2; // for centering text under icon

    // --- OPTIONAL TEXT ---
    if (config.text) {
      const t = new TranslationService();
      this.label = config.scene.add
        .text(0, offsetY + 6, t.translate(config.text), {
          fontFamily: 'korolev, sans-serif',
          fontSize: this.fontSize,
          color: new GameConfigService().getColorHex('--color-neutral-100'),
          ...config.textStyle,
        })
        .setOrigin(0.5, 0);

      this.add(this.label);
    }

    // Configure the hit area once.
    if (config.hitRadius) {
      this.setInteractive(
        new Phaser.Geom.Circle(0, 0, config.hitRadius),
        Phaser.Geom.Circle.Contains,
      );
      if (this.input) this.input.cursor = 'pointer';
    } else {
      // existing rectangular hit area
      const padding = 30 * DPR;
      const hitAreaWidth = this.label
        ? Math.max(this.iconSize, this.label.width) + padding
        : this.iconSize + padding;
      const hitAreaHeight = this.label
        ? this.iconSize + this.label.height + padding
        : this.iconSize + padding;

      this.setSize(hitAreaWidth, hitAreaHeight);
      this.setInteractive({ useHandCursor: true });
    }

    // Add to scene
    config.scene.add.existing(this);

    // Attach listeners once. Behavior depends on disabled state.
    this.enableInteractivity();

    // Apply initial disabled state, if any.
    this.applyDisabledState(!!config.isDisabled);
  }

  private enableInteractivity(): void {
    // Avoid accidental duplicate handlers if called multiple times.
    this.off(Phaser.Input.Events.POINTER_OVER);
    this.off(Phaser.Input.Events.POINTER_OUT);
    this.off(Phaser.Input.Events.POINTER_UP);

    this.on(Phaser.Input.Events.POINTER_OVER, () => {
      if (this.config.isDisabled) return;

      this.scene.tweens.add({
        targets: this,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 100,
        ease: 'Linear',
      });
    });

    this.on(Phaser.Input.Events.POINTER_OUT, () => {
      if (this.config.isDisabled) return;

      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 100,
        ease: 'Linear',
      });
    });

    this.on(Phaser.Input.Events.POINTER_UP, () => {
      if (this.config.isDisabled) {
        this.config.onDisabledClick?.();
        return;
      }

      this.config.onClick?.();
    });
  }

  private applyDisabledState(disabled: boolean): void {
    this.config.isDisabled = disabled;

    if (disabled) {
      this.icon.postFX.addColorMatrix().grayscale();
      this.setAlpha(0.5);
      this.setScale(1);
      // Only keep interactive when there is a dedicated disabled-click handler (e.g. Dice).
      // Otherwise remove interactivity entirely so pointer events are ignored.
      if (!this.config.onDisabledClick && this.input) {
        this.input.enabled = false;
      }
    } else {
      this.icon.postFX?.clear();
      this.setAlpha(1);
      if (this.input) this.input.enabled = true;
    }
  }

  public setDisabled(
    disabled: boolean,
    options?: { onDisabledClick?: () => void | Promise<void> },
  ) {
    if (options?.onDisabledClick) {
      this.config.onDisabledClick = options.onDisabledClick;
    }

    this.applyDisabledState(disabled);
  }
}
